import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  X,
  CalendarDays,
  PartyPopper,
  Sparkles,
  Music,
  Users,
  CalendarHeart,
  MapPin,
  ImageIcon,
  Mail,
  Phone,
  MessageCircle,
  Facebook,
  Instagram,
  Youtube,
  Heart,
  Tv,
  Clock,
} from 'lucide-react';

const SLOTS = [
  { key: 'week', label: 'Semaine', subtitle: 'Lundi au Vendredi', icon: <CalendarDays size={16} /> },
  { key: 'weekend', label: 'Week-end', subtitle: 'Samedi & Dimanche', icon: <PartyPopper size={16} /> },
  { key: 'special', label: 'Spécial Dimanche', subtitle: 'Dimanche 10h–20h', icon: <Sparkles size={16} /> },
];

const formatEventDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const formatShowDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const ChannelMenu = ({ open, onClose }) => {
  const [videos, setVideos] = useState({ week: [], weekend: [], special: [] });
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);
  const [shows, setShows] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState('programme');
  const [activeSlot, setActiveSlot] = useState('week');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    Promise.all([
      axios.get(`${API}/videos?schedule=week`).then((r) => r.data || []),
      axios.get(`${API}/videos?schedule=weekend`).then((r) => r.data || []),
      axios.get(`${API}/videos?schedule=special`).then((r) => r.data || []),
      axios.get(`${API}/artists`).then((r) => r.data || []),
      axios.get(`${API}/events`).then((r) => r.data || []),
      axios.get(`${API}/shows`).then((r) => r.data || []),
      axios.get(`${API}/settings`).then((r) => r.data || {}),
    ])
      .then(([w, we, sp, ar, ev, sh, st]) => {
        setVideos({ week: w, weekend: we, special: sp });
        setArtists(ar);
        setEvents(ev);
        setShows(sh);
        setSettings(st);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded]);

  // Close on Esc key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="channel-menu-overlay" data-testid="channel-menu-overlay" role="dialog" aria-modal="true">
      <div className="channel-menu-backdrop" onClick={onClose} />
      <div className="channel-menu-panel" data-testid="channel-menu-panel">
        <div className="channel-menu-header">
          <img
            src="https://customer-assets.emergentagent.com/job_bcc12bb2-d1c3-4c30-aab1-6d93b6ded326/artifacts/auol33u9_logo_4x.png"
            alt="Grace TV Gospel"
            className="channel-menu-logo"
          />
          <button
            type="button"
            onClick={onClose}
            className="channel-menu-close"
            aria-label="Fermer le menu"
            data-testid="channel-menu-close"
          >
            <X size={22} />
          </button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="channel-menu-tabs">
          <TabsList className="channel-menu-tabs-list" data-testid="channel-menu-tabs-list">
            <TabsTrigger value="programme" data-testid="menu-tab-programme" className="channel-menu-tab">
              <CalendarHeart size={18} /> Programme
            </TabsTrigger>
            <TabsTrigger value="shows" data-testid="menu-tab-shows" className="channel-menu-tab">
              <Tv size={18} /> Show TV
            </TabsTrigger>
            <TabsTrigger value="artistes" data-testid="menu-tab-artistes" className="channel-menu-tab">
              <Users size={18} /> Artistes
            </TabsTrigger>
            <TabsTrigger value="evenements" data-testid="menu-tab-evenements" className="channel-menu-tab">
              <Sparkles size={18} /> Événements
            </TabsTrigger>
            <TabsTrigger value="contact" data-testid="menu-tab-contact" className="channel-menu-tab">
              <Heart size={18} /> Crédit & Contact
            </TabsTrigger>
          </TabsList>

          <TabsContent value="programme" data-testid="menu-content-programme" className="channel-menu-content">
            <div className="programme-slot-tabs">
              {SLOTS.map((slot) => (
                <button
                  key={slot.key}
                  type="button"
                  className={`programme-slot-tab ${activeSlot === slot.key ? 'is-active' : ''}`}
                  onClick={() => setActiveSlot(slot.key)}
                  data-testid={`programme-slot-${slot.key}`}
                >
                  {slot.icon}
                  <div className="programme-slot-label">
                    <span>{slot.label}</span>
                    <small>{slot.subtitle}</small>
                  </div>
                </button>
              ))}
            </div>

            <div className="programme-list">
              {(videos[activeSlot] || []).length === 0 ? (
                <p className="menu-empty">Aucune vidéo programmée pour ce créneau.</p>
              ) : (
                videos[activeSlot].map((v, idx) => (
                  <div key={v.id} className="programme-item" data-testid={`programme-item-${activeSlot}-${idx}`}>
                    <div className="programme-num">{idx + 1}</div>
                    <Music size={18} className="programme-icon" />
                    <div className="programme-title">{v.title}</div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="shows" data-testid="menu-content-shows" className="channel-menu-content">
            {shows.length === 0 ? (
              <p className="menu-empty">Aucune émission programmée pour l'instant.</p>
            ) : (
              <div className="shows-list">
                {[...shows].map((s, idx) => {
                  const start = new Date(s.scheduled_at);
                  const totalMs = ((s.duration_minutes || 0) * 60 + (s.duration_seconds || 0)) * 1000;
                  const end = new Date(start.getTime() + totalMs);
                  const now = new Date();
                  let status = 'upcoming';
                  if (now >= start && now <= end) status = 'live';
                  else if (now > end) status = 'past';
                  const ytId = (s.youtube_url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/) || [])[1];
                  const watchUrl = ytId ? `https://www.youtube.com/watch?v=${ytId}` : s.youtube_url;
                  return (
                    <div key={s.id} className={`menu-event-card show-card show-status-${status}`} data-testid={`menu-show-${idx}`}>
                      <div className="menu-event-image-wrap">
                        {s.image_url ? (
                          <img src={s.image_url} alt={s.title} className="menu-event-image" />
                        ) : (
                          <div className="menu-event-image placeholder">
                            <Tv size={28} />
                          </div>
                        )}
                      </div>
                      <div className="menu-event-info">
                        <h4>
                          {s.title}
                          {status === 'live' && <span className="show-badge show-badge-live">EN DIRECT</span>}
                          {status === 'upcoming' && <span className="show-badge show-badge-upcoming">À venir</span>}
                          {status === 'past' && <span className="show-badge show-badge-past">Replay</span>}
                        </h4>
                        <div className="menu-event-meta">
                          <span>
                            <CalendarDays size={14} /> {formatShowDateTime(s.scheduled_at)}
                          </span>
                          <span>
                            <Clock size={14} /> {(() => {
                              const m = s.duration_minutes || 0;
                              const sec = s.duration_seconds || 0;
                              if (m === 0 && sec === 0) return '0 sec';
                              const parts = [];
                              if (m > 0) parts.push(`${m} min`);
                              if (sec > 0) parts.push(`${sec} sec`);
                              return parts.join(' ');
                            })()}
                          </span>
                        </div>
                        {s.description && <p>{s.description}</p>}
                        {status === 'past' && (
                          <a
                            href={watchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="show-replay-link"
                            data-testid={`menu-show-replay-${idx}`}
                          >
                            <Tv size={14} /> Regarder le replay sur YouTube
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="artistes" data-testid="menu-content-artistes" className="channel-menu-content">
            {artists.length === 0 ? (
              <p className="menu-empty">Aucun artiste pour l'instant.</p>
            ) : (
              <div className="artists-grid">
                {artists.map((a, idx) => (
                  <div key={a.id} className="artist-card" data-testid={`menu-artist-${idx}`}>
                    <div className="artist-photo-wrap">
                      {a.photo_url ? (
                        <img src={a.photo_url} alt={a.name} className="artist-photo" />
                      ) : (
                        <div className="artist-photo placeholder">
                          <ImageIcon size={28} />
                        </div>
                      )}
                    </div>
                    <div className="artist-info">
                      <h4>{a.name}</h4>
                      {a.bio && <p>{a.bio}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="evenements" data-testid="menu-content-evenements" className="channel-menu-content">
            {events.length === 0 ? (
              <p className="menu-empty">Aucun événement à venir pour l'instant.</p>
            ) : (
              <div className="events-list">
                {events.map((ev, idx) => (
                  <div key={ev.id} className="menu-event-card" data-testid={`menu-event-${idx}`}>
                    <div className="menu-event-image-wrap">
                      {ev.image_url ? (
                        <img src={ev.image_url} alt={ev.title} className="menu-event-image" />
                      ) : (
                        <div className="menu-event-image placeholder">
                          <ImageIcon size={28} />
                        </div>
                      )}
                    </div>
                    <div className="menu-event-info">
                      <h4>{ev.title}</h4>
                      <div className="menu-event-meta">
                        {ev.event_date && (
                          <span>
                            <CalendarDays size={14} /> {formatEventDate(ev.event_date)}
                          </span>
                        )}
                        {ev.location && (
                          <span>
                            <MapPin size={14} /> {ev.location}
                          </span>
                        )}
                      </div>
                      {ev.description && <p>{ev.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contact" data-testid="menu-content-contact" className="channel-menu-content">
            <div className="contact-wrapper">
              {settings.description && (
                <div className="contact-block">
                  <h3 className="contact-title">À propos de Grace TV Gospel</h3>
                  <p className="contact-description">{settings.description}</p>
                </div>
              )}

              <div className="contact-block">
                <h3 className="contact-title">Nous contacter</h3>
                <ul className="contact-list">
                  {settings.email && (
                    <li>
                      <a href={`mailto:${settings.email}`} data-testid="contact-email">
                        <Mail size={18} />
                        <span>{settings.email}</span>
                      </a>
                    </li>
                  )}
                  {settings.phone && (
                    <li>
                      <a href={`tel:${settings.phone.replace(/\s+/g, '')}`} data-testid="contact-phone">
                        <Phone size={18} />
                        <span>{settings.phone}</span>
                      </a>
                    </li>
                  )}
                  {settings.whatsapp && (
                    <li>
                      <a
                        href={`https://wa.me/${settings.whatsapp.replace(/[^\d+]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-whatsapp"
                      >
                        <MessageCircle size={18} />
                        <span>WhatsApp · {settings.whatsapp}</span>
                      </a>
                    </li>
                  )}
                  {settings.facebook_url && (
                    <li>
                      <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook">
                        <Facebook size={18} />
                        <span>Facebook</span>
                      </a>
                    </li>
                  )}
                  {settings.instagram_url && (
                    <li>
                      <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram">
                        <Instagram size={18} />
                        <span>Instagram</span>
                      </a>
                    </li>
                  )}
                  {settings.youtube_url && (
                    <li>
                      <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="contact-youtube">
                        <Youtube size={18} />
                        <span>YouTube</span>
                      </a>
                    </li>
                  )}
                </ul>
                {!settings.email &&
                  !settings.phone &&
                  !settings.whatsapp &&
                  !settings.facebook_url &&
                  !settings.instagram_url &&
                  !settings.youtube_url && (
                    <p className="menu-empty">
                      Les coordonnées seront ajoutées prochainement.
                    </p>
                  )}
              </div>

              {settings.credits && (
                <div className="contact-block">
                  <h3 className="contact-title">Crédits</h3>
                  <p className="contact-description contact-credits">{settings.credits}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ChannelMenu;
